import { Contact } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { isContactPickerSupported } from '@/lib/contact-picker';

/**
 * Minimal typing for the browser Contact Picker API.
 *
 * The Contact Picker API (https://developer.mozilla.org/docs/Web/API/Contact_Picker_API)
 * is only available on Chromium-based browsers on Android. It is not part of the
 * standard TypeScript DOM lib, so we declare the slice we use here. We deliberately
 * read nothing back to the server — the picked name and phone are written straight
 * into the form fields and the device's contact list is never stored or transmitted.
 */
interface ContactInfo {
  name?: string[];
  tel?: string[];
}

interface ContactsManager {
  select(
    properties: Array<'name' | 'tel' | 'email' | 'address' | 'icon'>,
    options?: { multiple?: boolean },
  ): Promise<ContactInfo[]>;
}

interface NavigatorWithContacts extends Navigator {
  contacts: ContactsManager;
}

interface PickedContact {
  name: string;
  phone: string;
}

interface Props {
  onPick: (contact: PickedContact) => void;
  label?: string;
}

/**
 * Progressive-enhancement button that opens the device phone book and pre-fills the
 * invite form with the chosen contact's name and first phone number. Renders nothing
 * on browsers without the Contact Picker API (desktop, iOS Safari), where users keep
 * typing manually. Nothing is stored or sent — fill-only, mirroring the mobile apps.
 */
export function DeviceContactButton({ onPick, label = 'Pick from contacts' }: Props) {
  const [busy, setBusy] = useState(false);

  if (!isContactPickerSupported()) return null;

  const handleClick = async () => {
    setBusy(true);
    try {
      const results = await (navigator as NavigatorWithContacts).contacts.select(['name', 'tel'], {
        multiple: false,
      });
      const picked = results[0];
      if (!picked) return;
      const name = picked.name?.[0]?.trim() ?? '';
      const phone = picked.tel?.[0]?.trim() ?? '';
      if (!name && !phone) return;
      onPick({ name, phone });
    } catch {
      // User cancelled the picker or permission was denied — nothing to do.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={busy}
      onClick={() => void handleClick()}
      className="w-full"
    >
      <Contact className="size-4" />
      {label}
    </Button>
  );
}
